import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { listOfferedClasses, buyOfferedClass } from '../api';
import { goTo } from './urls';

function BuyButton({ cls }) {
  const [seats, setSeats] = useState('1'); // string so the field can be cleared while typing
  const seatCount = Math.max(1, parseInt(seats, 10) || 1);
  const buy = useMutation({
    mutationFn: () => buyOfferedClass(cls.id, { seats: seatCount, return_url: window.location.href }),
    onSuccess: (res) => {
      if (!res?.checkout_url) return;
      if (!goTo(res.checkout_url)) toast.error('That checkout link looks invalid');
    },
    onError: (e) => toast.error(e?.message || 'Could not start checkout'),
  });
  return (
    <div className="flex items-center gap-2 mt-3">
      <input type="number" min="1" value={seats} aria-label="Number of seats"
        onChange={(e) => setSeats(e.target.value)}
        onBlur={() => setSeats(String(seatCount))}
        className="w-16 text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-transparent" />
      <span className="text-xs text-gray-500">seats</span>
      <button onClick={() => buy.mutate()} disabled={buy.isPending}
        className="ml-auto text-xs font-bold px-3 py-1.5 rounded-lg bg-blue-600 text-white disabled:opacity-40">
        Buy seats
      </button>
    </div>
  );
}

export default function OfferedClasses() {
  const { data, isLoading, isError } = useQuery({ queryKey: ['offered-classes'], queryFn: listOfferedClasses });
  if (isLoading) return <div className="text-sm text-gray-500">Loading...</div>;
  if (isError) return <div className="text-sm text-red-500">Could not load the class catalog. Please try again.</div>;
  const classes = data?.classes || [];
  if (classes.length === 0) {
    return <div className="text-sm text-gray-500">No classes are offered yet. Use "Request a Class" to ask for one.</div>;
  }
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {classes.map((c) => (
        <div key={c.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex flex-col">
          <div className="font-semibold text-sm">{c.title}</div>
          {c.summary && <div className="text-xs text-gray-500 mt-1">{c.summary}</div>}
          <div className="text-[11px] text-gray-400 mt-2">
            {[c.level, c.delivery, c.minutes && `${c.minutes} min`].filter(Boolean).join(' · ')}
          </div>
          {c.buyable && (
            <div className="mt-2 text-xs font-semibold text-blue-600">
              ${(c.seat_price_cents / 100).toFixed(2)} / seat
              {/* The setup fee is charged once per class per buyer, so the server
                  sends 0 here once you own it. Quoting it again would not match
                  what Stripe charges. */}
              {c.dev_setup_fee_cents ? ` · $${(c.dev_setup_fee_cents / 100).toFixed(2)} setup` : ''}
              {c.owned && <span className="ml-2 font-normal text-gray-500">you own this class</span>}
            </div>
          )}
          <div className="mt-auto">
            {c.buyable
              ? <BuyButton cls={c} />
              : <div className="text-[11px] text-gray-400 mt-3">Contact us to purchase this class.</div>}
          </div>
        </div>
      ))}
    </div>
  );
}
